import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, Row, Col, Form, Button, InputGroup, Badge, Spinner } from "react-bootstrap";
import { CONFIG } from "../utils/config";
import TemplateInterface from "./TemplateInterface";

function TemplateManager({ addLog, saveTransaction, notify }) {
  const [templates, setTemplates] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({ id: "", name: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${CONFIG.DB_BASE}/templates`);
      setTemplates(res.data);
    } catch (e) {
      addLog("Error loading templates: " + e.message, "ERROR");
    } finally {
      setLoading(false);
    }
  }, [addLog]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    if (!formData.id) return alert("Template ID is required");
    try {
      const payload = { TemplateId: formData.id, FriendlyName: formData.name || `Template ${formData.id}` };
      if (isEditing) { await axios.delete(`${CONFIG.DB_BASE}/templates/${editId}`); }
      await axios.post(`${CONFIG.DB_BASE}/templates`, payload);
      notify(isEditing ? "Template Updated Successfully" : "Template Added Successfully");
      addLog(`Template ${isEditing ? 'Updated' : 'Created'}: ${formData.id}`, "SUCCESS");
      setFormData({ id: "", name: "" });
      setIsEditing(false);
      loadData();
    } catch (e) {
      addLog(e.message, "ERROR");
    }
  };

  const handleDelete = async (dbId) => {
    if(!window.confirm("Confirm Deletion?")) return;
    try {
      await axios.delete(`${CONFIG.DB_BASE}/templates/${dbId}`);
      notify("Template Deleted", "warning");
      loadData();
    } catch (e) { addLog(e.message, "ERROR"); }
  };

  const prepareEdit = (t) => {
    setIsEditing(true);
    setEditId(t.id);
    setFormData({ id: t.templateId, name: t.friendlyName });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filtered = templates.filter(t => t.friendlyName.toLowerCase().includes(filterText.toLowerCase()) || t.templateId.includes(filterText));

  if (selectedTemplate) {
    return <TemplateInterface templateId={selectedTemplate} onBack={() => setSelectedTemplate(null)} addLog={addLog} saveTransaction={saveTransaction} notify={notify} />;
  }

  return (
    <div className="fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div><h4 className="fw-bold text-primary m-0">Template Database</h4><small className="text-muted">Manage your DLT Templates locally</small></div>
        <Badge bg="secondary" className="px-3 py-2 fs-6">{templates.length} Stored</Badge>
      </div>

      <Card className="border-0 shadow-sm bg-white mb-4">
        <Card.Body>
          <h6 className="fw-bold text-muted mb-3 border-bottom pb-2">
            {isEditing ? <span className="text-warning"><i className="bi bi-pencil me-2"></i>EDITING TEMPLATE</span> : <span className="text-success"><i className="bi bi-plus-circle me-2"></i>ADD NEW TEMPLATE</span>}
          </h6>
          <Row className="g-2">
            <Col md={5}><Form.Group><Form.Label className="small fw-bold text-muted">DLT Template ID</Form.Label><Form.Control placeholder="e.g. 100746XXXX" value={formData.id} onChange={e => setFormData({...formData, id: e.target.value})} /></Form.Group></Col>
            <Col md={5}><Form.Group><Form.Label className="small fw-bold text-muted">Friendly Name</Form.Label><Form.Control placeholder="e.g. OTP Message" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></Form.Group></Col>
            <Col md={2} className="d-flex align-items-end">
              {isEditing ? (
                <div className="d-flex gap-2 w-100">
                  <Button variant="warning" className="flex-grow-1 fw-bold text-white" onClick={handleSave}>Update</Button>
                  <Button variant="outline-secondary" onClick={() => { setIsEditing(false); setFormData({id:"", name:""}); }}>Cancel</Button>
                </div>
              ) : (
                <Button variant="success" className="w-100 fw-bold" onClick={handleSave}><i className="bi bi-save me-2"></i> Save</Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text className="bg-white"><i className="bi bi-search"></i></InputGroup.Text>
          <Form.Control type="search" placeholder="Filter stored templates..." value={filterText} onChange={e => setFilterText(e.target.value)} />
        </InputGroup>
      </div>

      {loading ? <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div> : (
        <div className="list-group shadow-sm">
          {filtered.length === 0 && <div className="text-center p-5 text-muted border bg-light rounded">No templates found matching your criteria.</div>}
          {filtered.map(t => (
            <div key={t.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-3">
              <div><div className="fw-bold text-dark fs-5">{t.friendlyName}</div><div className="text-muted font-monospace small"><i className="bi bi-hash me-1"></i>{t.templateId}</div></div>
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" className="px-3 fw-bold" onClick={() => setSelectedTemplate(t.templateId)}>Use Template</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => prepareEdit(t)}>Edit</Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(t.id)}>Del</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TemplateManager;